import { Badge, Button, Card } from "react-bootstrap"
import { Link } from "react-router-dom"
import { AmenityType } from "../../../Types/amenityTypes"
import { useState } from "react"
import canAccess from "../../../services/accessService/accessService"

export default function AmenityEntry(props: {
    amenityData: AmenityType,
    fullView?: boolean,
    showActions?: boolean,
    customButtons?: {
        onClick: (amenityData: AmenityType) => void,
        text: string,
        variant?: string,
        disabled?: boolean,
    }[]
}) {

    const {
        amenityData,
        fullView: initialFullView = false,
        showActions = true,
        customButtons,
    } = props

    const [fullView, setFullView] = useState(initialFullView)

    const {
        _id,
        slug,
        title,
        description,
        image,
        icon,
        tags,
        type,
        basePrice,
        baseQuantity,
        isActive,
        featured,
        providerSettings = []
    } = amenityData

    const getTypeVariant = (type: string) => {
        switch (type) {
            case 'included': return 'success'
            case 'paid': return 'warning'
            default: return 'secondary'
        }
    }

    return (
        <Card className="mb-3">
            <Card.Body>
                <Card.Title>
                    {title}
                </Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                    {slug}
                </Card.Subtitle>

                <Card.Text className="mb-1 small">
                    <strong>Type:</strong> <Badge bg={getTypeVariant(type)}>{type}</Badge>
                </Card.Text>

                {tags && tags.length > 0 && (
                    <Card.Text className="mb-1 small">
                        <strong>Tags:</strong>{' '}
                        {tags.map((tag, idx) => (
                            <Badge key={idx} bg="info" className="me-1">{tag}</Badge>
                        ))}
                    </Card.Text>
                )}

                {providerSettings && providerSettings.length > 0 && (
                    <Card.Text className="mb-1 small">
                        <strong>Provider Settings:</strong>{' '}
                        {providerSettings.map((setting, idx) => (
                            <Badge key={idx} bg="secondary" className="me-1">{setting.provider}</Badge>
                        ))}
                    </Card.Text>
                )}

                {fullView && (
                    <>
                        <hr />
                        <Card.Subtitle className="mb-2">
                            <strong>Details:</strong>
                        </Card.Subtitle>
                        {description && (
                            <Card.Text className="mb-1 small">
                                <strong>Description:</strong><br />
                                {description}
                            </Card.Text>
                        )}
                        {image && (
                            <Card.Text className="mb-1 small">
                                <strong>Image:</strong>{' '}
                                <a href={image} target="_blank" rel="noreferrer">View Image</a>
                            </Card.Text>
                        )}
                        {icon && (
                            <Card.Text className="mb-1 small">
                                <strong>Icon:</strong>{' '}
                                <a href={icon} target="_blank" rel="noreferrer">View Icon</a>
                            </Card.Text>
                        )}
                        {basePrice !== undefined && (
                            <Card.Text className="mb-1 small">
                                <strong>Base Price:</strong> ${basePrice}
                            </Card.Text>
                        )}
                        {baseQuantity !== undefined && (
                            <Card.Text className="mb-1 small">
                                <strong>Base Quantity:</strong> {baseQuantity}
                            </Card.Text>
                        )}
                        <Card.Text className="mb-1 small">
                            <strong>Active:</strong> {isActive ? 'Yes' : 'No'}
                        </Card.Text>
                        <Card.Text className="mb-1 small">
                            <strong>Featured:</strong> {featured ? 'Yes' : 'No'}
                        </Card.Text>
                    </>
                )}

            </Card.Body>

            {showActions && (
                <Card.Footer>

                    <div>
                        <Badge bg={isActive ? 'success' : 'secondary'} className="me-2 mb-2">
                            {isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {featured && (
                            <Badge bg="primary" className="me-2 mb-2">
                                Featured
                            </Badge>
                        )}
                    </div>

                    {!initialFullView && canAccess('routes', 'amenity') && (
                        <Link className="btn btn-primary btn-sm me-2 mb-2" to={`/amenity/${_id}`}>
                            View
                        </Link>
                    )}
                    {canAccess('routes', 'edit-amenity') && (
                        <Link className="btn btn-secondary btn-sm me-2 mb-2" to={`/edit-amenity/${_id}`}>
                            Edit
                        </Link>
                    )}
                    {canAccess('routes', 'delete-amenity') && (
                        <Link className="btn btn-danger btn-sm me-2 mb-2" to={`/delete-amenity/${_id}`}>
                            Delete
                        </Link>
                    )}
                    {!initialFullView && (
                        <Button
                            className="me-2 mb-2"
                            variant="secondary"
                            size="sm"
                            onClick={() => setFullView(!fullView)}
                        >
                            {fullView ? 'Hide Details' : 'Show Details'}
                        </Button>
                    )}
                    {customButtons && customButtons.map((btn, idx) => (
                        <Button
                            key={`custom-btn-${idx}`}
                            className="me-2 mb-2"
                            variant={btn.variant || 'primary'}
                            size="sm"
                            onClick={() => btn.onClick(amenityData)}
                            disabled={btn.disabled}
                        >
                            {btn.text}
                        </Button>
                    ))}
                </Card.Footer>
            )}
        </Card>
    )
}
